// client/src/theme/palettes.js
//
// GENERATED — do not edit.
//
// Copied from packages/core/src/theme/palettes.js by tools/sync-palettes.py,
// because packages/core is not a workspace the CRA build can resolve. The four
// palettes are one set of design tokens and both clients must build from the
// same numbers: a school that picks Oxford Blue should not get two different
// blues depending on which app it opens, and one that picks Dark Mode should
// not get a dark phone and a white desktop.
//
// Edit the core file and re-run the script. See the header there for what each
// role in a palette is FOR.

export const PALETTES = [
  {
    // THE DEFAULT. Crisp blue on white — closest to the scheme this product has
    // always had, so a school that never opens the picker keeps what it knows.
    id: 'bluewhite',
    name: 'Classic Blue & White',
    tagline: 'Crisp blue on white — clean, trustworthy, effortlessly professional.',
    dark: false,
    deep: '#0A4E9B', mid: '#116CDC', soft: '#3D96F7',
    accent: '#0E95F9', accentDeep: '#0572C8', accentSoft: '#9ED9FD',
    text: '#16233A', muted: '#5A6B82', border: '#DCE6F0',
    bg: '#F6F9FC', card: '#FFFFFF', tint: '#E7F1FB', tintAccent: '#E3F1FB',
    success: '#29825C', successBg: '#E4F2EA', successTx: '#1E5A40',
    warning: '#DA8B14', warningBg: '#F8EFD8', warningTx: '#8A5E1D',
    danger: '#D32A18', dangerBg: '#F7E1DE', dangerTx: '#8A2A22',
    // The DEEPER of the palette's two blues is the ink and the lighter is the
    // mark, which is the opposite of how the source file lists them. Both values
    // are its own — Blue Mid and Azure Deep — but read the other way round the
    // pair lands at 4.43:1 on the info ground, just under the line.
    info: '#116CDC', infoBg: '#EAF0F9', infoTx: '#1D6FB0',
  },
  {
    id: 'oxford',
    name: 'Oxford Blue & Gold',
    tagline: 'The Ivy League standard — prestigious, trustworthy, timeless.',
    dark: false,
    deep: '#142751', mid: '#203E80', soft: '#2367D0',
    accent: '#E29E0D', accentDeep: '#B17B0E', accentSoft: '#F9D770',
    text: '#1E293B', muted: '#64748B', border: '#E4E7EC',
    bg: '#FAF8F3', card: '#FFFFFF', tint: '#EAF0F9', tintAccent: '#F7EFD9',
    success: '#29825C', successBg: '#E4F2EB', successTx: '#1C5A40',
    warning: '#DA9114', warningBg: '#FBF0D9', warningTx: '#8A611D',
    danger: '#B62C2C', dangerBg: '#F7E3E3', dangerTx: '#7E2A2A',
    info: '#203E80', infoBg: '#E8EDF6', infoTx: '#243657',
  },
  {
    // `mid` is Slate and `soft` is Steel — the two lighter steps of the primary
    // ramp. The amber stays ORNAMENT: a graphite scheme with amber buttons
    // reads as a page full of warnings rather than as a brand.
    id: 'graphite',
    name: 'Classic Dark Grey',
    tagline: 'Graphite & amber — sophisticated, neutral, quietly premium.',
    dark: false,
    deep: '#292F38', mid: '#3A4250', soft: '#566377',
    accent: '#DB952F', accentDeep: '#AB7527', accentSoft: '#F6D086',
    text: '#1F2328', muted: '#6B7280', border: '#E3E5E8',
    bg: '#F5F5F3', card: '#FFFFFF', tint: '#E9EDF3', tintAccent: '#F3EEE3',
    success: '#29825C', successBg: '#E3F1EA', successTx: '#1E5A40',
    warning: '#DA8B14', warningBg: '#F7EED9', warningTx: '#8A5E1D',
    danger: '#B62C2C', dangerBg: '#F5E2E2', dangerTx: '#7E2A2A',
    info: '#395F8B', infoBg: '#E9EDF3', infoTx: '#33465F',
  },
  {
    // THE DARK ONE. `deep` is the SURFACE, not the darkest value in the set:
    // on a dark scheme the chrome sits ABOVE the page rather than below it, and
    // a sidebar painted the page colour disappears into the thing it is meant
    // to stand on.
    //
    // `text` and `muted` are LIGHT inks here, and every derived token follows
    // from them — which is exactly why each client maps roles onto its shade
    // ladder rather than hard-coding colours.
    id: 'darkmode',
    name: 'Dark Mode',
    tagline: 'Deep slate with blue & gold — comfortable, focused, elegant after dark.',
    dark: true,
    deep: '#161E29', mid: '#5089FB', soft: '#89B2FC',
    accent: '#F2B430', accentDeep: '#CE911D', accentSoft: '#FBD379',
    text: '#E8ECF2', muted: '#97A2B3', border: '#2A323F',
    bg: '#0E131A', card: '#181E27', tint: '#212936', tintAccent: '#2C2718',
    success: '#37C788', successBg: '#14301F', successTx: '#5FD3A0',
    warning: '#F9B125', warningBg: '#322611', warningTx: '#E8BE6A',
    danger: '#F6574E', dangerBg: '#351A1A', dangerTx: '#F0857E',
    info: '#419AFA', infoBg: '#172436', infoTx: '#7FB2EE',
  },
];

/**
 * The palette a school gets if nobody has chosen.
 *
 * Classic Blue & White — closest to the scheme the product has always had. A
 * school that has not asked for anything different must keep what it knows.
 */
export const DEFAULT_PALETTE_ID = 'bluewhite';

/** Where the choice is remembered. Shared, so both clients read one key. */
export const PALETTE_STORAGE_KEY = 'appearance:palette';

/** A palette by id, falling back to the default rather than to undefined. */
export function paletteById(id) {
  return PALETTES.find((p) => p.id === id)
    || PALETTES.find((p) => p.id === DEFAULT_PALETTE_ID)
    || PALETTES[0];
}

/**
 * A hex colour at a given opacity.
 *
 * Neither platform has `color-mix`, and a tinted rule written as a second hex
 * someone eyeballed is how a hairline ends up a slightly different colour from
 * the mark it belongs to.
 */
export function withAlpha(hex, alpha) {
  const h = String(hex || '').replace('#', '');
  const n = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(n.slice(i, i + 2), 16));
  if ([r, g, b].some((v) => Number.isNaN(v))) return `rgba(0, 0, 0, ${alpha})`;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Whether a colour is light enough to need dark text on it.
 *
 * The perceptual weighting (299/587/114) rather than a plain average: the eye
 * is far more sensitive to green than to blue, and an unweighted mean calls
 * saturated blues light when white sits on them perfectly well.
 */
export function isLight(hex) {
  const h = String(hex || '').replace('#', '');
  const n = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(n.slice(i, i + 2), 16));
  if ([r, g, b].some((v) => Number.isNaN(v))) return false;
  return (r * 299 + g * 587 + b * 114) / 1000 > 150;
}

/** WCAG relative luminance. */
function luminance(hex) {
  const h = String(hex || '').replace('#', '');
  const n = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const [r, g, b] = [0, 2, 4].map((i) => {
    const c = parseInt(n.slice(i, i + 2), 16) / 255;
    return Number.isNaN(c) ? 0 : (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two colours, 1:1 to 21:1. */
export function contrastRatio(a, b) {
  const [hi, lo] = [luminance(a), luminance(b)].sort((m, n) => n - m);
  return (hi + 0.05) / (lo + 0.05);
}

/** The two inks anything is ever written in. */
const INK_DARK = '#101418';
const INK_LIGHT = '#FFFFFF';

/**
 * Text that reads on `hex` — the better of the two inks, MEASURED.
 *
 * NOT `palette.text`: on the dark palette that is a light ink, and handing it
 * back for a light background would be white on white. The decision is about
 * the background in hand, never about the scheme it belongs to.
 *
 * And not `isLight` either, which is a cheap threshold and wrong exactly where
 * it matters. The dark palette's action blue (#5B8DEF) sits just under that
 * threshold, so a threshold answers "white" — and white on it is 3.23:1, a
 * failure, where the dark ink is 6.4:1. Mid-tone blues and greens are where a
 * threshold guesses and a ratio knows, and every palette's action colour is a
 * mid-tone blue or green.
 */
export function inkOn(palette, hex) {
  return contrastRatio(INK_DARK, hex) >= contrastRatio(INK_LIGHT, hex)
    ? INK_DARK
    : INK_LIGHT;
}

/**
 * THE CHROME, derived rather than hand-written per palette.
 *
 * On a LIGHT palette the sidebar and top bar are a deep ground with a light
 * foreground — the inverse of everything else on screen — so reaching for the
 * ordinary text colour there gives near-black on a near-black bar.
 *
 * ON A DARK PALETTE THE RELATIONSHIP IS NOT INVERTED, it is flat: the chrome is
 * a surface one step ABOVE the page, wearing the same light ink as everything
 * else. The active band is the one thing that still has to change, and for the
 * opposite reason to the light case: there it borrows the page ground to stand
 * out, but on a dark scheme the page is DARKER than the bar, so a band that
 * borrowed it would recede. It takes the accent's soft shade instead — the only
 * value in a dark palette bright enough to say "you are here".
 */
export function chromeFor(palette) {
  if (palette.dark) {
    return {
      bg: palette.deep,
      bgDeep: palette.deep,
      accent: palette.deep,
      text: palette.text,
      icon: withAlpha(palette.text, 0.76),
      line: withAlpha(palette.text, 0.12),
      hover: withAlpha(palette.text, 0.08),
      active: palette.tint,
      activeText: palette.accentSoft,
      activeHover: palette.border,
      subBg: withAlpha('#FFFFFF', 0.05),
      subText: withAlpha(palette.text, 0.82),
      muted: withAlpha(palette.text, 0.68),
    };
  }

  return {
    bg: palette.deep,
    // The same value on purpose: a bar told to be one colour must measure that
    // colour everywhere. When these differed the bar was a gradient and only
    // its topmost row of pixels was ever the colour that had been set.
    bgDeep: palette.deep,
    accent: palette.deep,
    text: 'rgba(255,255,255,0.92)',
    icon: 'rgba(255,255,255,0.76)',
    line: 'rgba(255,255,255,0.12)',
    hover: 'rgba(255,255,255,0.10)',
    // The current page is the app's own surface showing through the menu, so it
    // takes the card colour and flips its ink to the bar's own.
    active: palette.card,
    activeText: palette.deep,
    activeHover: palette.tint,
    // A step LIGHTER than the rail, not darker: a recess this deep reads as a
    // hole cut into the bar rather than as a nested list.
    subBg: 'rgba(255,255,255,0.08)',
    subText: 'rgba(255,255,255,0.82)',
    // 68%, NOT 62%. Group headings are the smallest type in the chrome and they
    // sit on whichever `deep` the palette supplies — on the lightest of those
    // (Blue & White's #1B4F8A) 62% lands at 4.26:1. The other three passed it
    // comfortably, which is how a value like this survives: it is only wrong
    // under the palette nobody checked it against.
    muted: 'rgba(255,255,255,0.68)',
  };
}

/**
 * The categorical order for charts, in this palette's own colours.
 *
 * ORDER IS THE COLOUR-VISION MECHANISM, not decoration: slots are filled from
 * the first downward so neighbouring series stay separable, and past the eighth
 * a caller must fold the tail into "Other" rather than cycling.
 */
export function categoricalFor(palette) {
  return [
    palette.mid,
    palette.accent,
    palette.info,
    palette.success,
    palette.danger,
    palette.soft,
    palette.warning,
    palette.accentDeep,
  ];
}
